import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { StorageService } from './storage';

/** Matches the API LoginType enum (1 = Password, 2 = OTP). */
export enum LoginType {
  Password = 1,
  Otp = 2,
}

/** POST /api/Login/Login -> LoginRequest */
export interface LoginRequest {
  identifier: string;
  password?: string | null;
  OTP?: number | null;
  loginType: LoginType;
  rememberMe: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private readonly baseUrl = `${environment.apiUrl}/Login`;

  // =========================
  // LOGIN (PASSWORD)
  // =========================
  passwordLogin(identifier: string, password: string, rememberMe = false) {
    const body: LoginRequest = {
      identifier,
      password,
      loginType: LoginType.Password,
      rememberMe,
    };

    return this.http.post(`${this.baseUrl}/Login`, body);
  }

  // =========================
  // LOGIN (OTP)
  // =========================
  OTPLogin(identifier: string, otp: number, rememberMe = false) {
    const body: LoginRequest = {
      identifier,
      OTP: otp,
      loginType: LoginType.Otp,
      rememberMe,
    };

    return this.http.post(`${this.baseUrl}/Login`, body);
  }

  // =========================
  // OTP APIs
  // =========================
  sendOtp(identifier: string) {
    return this.http.post(`${this.baseUrl}/SendOTP`, { identifier });
  }

  resendOtp(identifier: string) {
    return this.http.post(`${this.baseUrl}/ResendOTP`, { identifier });
  }

  // =========================
  // RESET PASSWORD (forgot password flow)
  // =========================
  resetPassword(identifier: string, otp: number, newPassword: string) {
    return this.http.post(`${this.baseUrl}/ResetPassword`, {
      identifier,
      otp,
      newPassword,
    });
  }

  // verifyOtp(identifier: string, otp: number) {
  //   return this.http.post(`${this.baseUrl}/VerifyOTP`, { identifier, otp });
  // }

  validateUser(identifier: string) {
    return this.http.post(`${this.baseUrl}/ValidateUser`, { identifier });
  }

  // =========================
  // CHANGE PASSWORD (authenticated)
  // =========================
  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post(`${this.baseUrl}/ChangePassword`, {
      currentPassword,
      newPassword,
    });
  }

  // =========================
  // REFRESH TOKEN
  // =========================
  refreshToken() {
    const refreshToken = this.storage.getLocal('refreshToken');

    return this.http.post(`${this.baseUrl}/RefreshToken`, {
      refreshToken: refreshToken,
    });
  }

  // =========================
  // LOGOUT
  // =========================
  logout() {
    const refreshToken = this.storage.getLocal('refreshToken');

    return this.http.post(`${this.baseUrl}/Logout`, {
      refreshToken,
    });
  }

  // =========================
  // SAVE AUTH
  // =========================
saveAuth(res: any): void {
  const data = res?.data;

  if (data?.token) this.storage.setLocal('token', data.token);
  if (data?.refreshToken) this.storage.setLocal('refreshToken', data.refreshToken);

  // Only the LOGIN response carries the identity block. A RefreshToken response is just
  // { token, refreshToken } — writing it here used to wipe the stored user (roles became
  // undefined), so after every silent refresh isAdmin() flipped false and admins were
  // bounced onto partner pages. Keep the existing user unless a real identity arrives.
  if (data?.user_id != null) {
    const user = {
      user_id: data.user_id,
      username: data.username,
      partnerCode: data.partnerCode,
      roles: data.roles,
    };
    this.storage.setLocal('user', JSON.stringify(user));
  }
}

  // =========================
  // HELPERS
  // =========================
  getToken(): string | null {
    return this.storage.getLocal('token');
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  /** The stored user object ({ user_id, username, partnerCode, roles }) or null. */
  getUser(): any {
    const raw = this.storage.getLocal('user');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** Roles as a lowercase string array. */
  getRoles(): string[] {
    const roles = this.getUser()?.roles;
    return Array.isArray(roles) ? roles.map((r: any) => String(r).toLowerCase()) : [];
  }

  /** Admins are never gated by KYC and get the full app. */
  isAdmin(): boolean {
    return this.getRoles().includes('admin');
  }
}
